import { Controller } from 'react-hook-form';
import { Label } from 'reactstrap';

// sample usage:
// <SwitchButton name="isActive" control={control} label="Status" />
// <SwitchButton name="isActive" control={control} label="Status" onLabel="ON" offLabel="OFF" />

const SwitchButton = ({
    name,
    control,
    label,
    onLabel = "",
    offLabel = "",
    defaultValue = false,
    disabled = false,
    height = 32,
    width,
    ...props
}) => {
    const hasText = Boolean(onLabel || offLabel);

    // Bare pill when no text; wider track when there is
    const trackWidth = width ?? (hasText ? Math.max(88, height * 2.75) : height * 1.9);
    const knob = height - 10;
    const pad = 4;

    return (
        <div className="mb-3">
            {label && (
                <Label className="form-label d-block mb-1" for={name}>
                    {label}
                </Label>
            )}

            <Controller
                name={name}
                control={control}
                defaultValue={defaultValue}
                render={({ field }) => {
                    const checked = !!field.value;

                    return (
                        <button
                            type="button"
                            id={name}
                            role="switch"
                            aria-checked={checked}
                            aria-label={label || name}
                            disabled={disabled}
                            onClick={() => field.onChange(!checked)}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            {...props}
                            className="dxo-switch"
                            data-checked={checked ? "true" : "false"}
                            style={{
                                position: "relative",
                                display: "inline-block",
                                width: trackWidth,
                                height,
                                padding: 0,
                                boxSizing: "border-box",
                                border: checked
                                    ? "1px solid #1e5f8a"
                                    : "1px solid var(--vz-border-color)",
                                borderRadius: height / 2,
                                cursor: disabled ? "not-allowed" : "pointer",
                                opacity: disabled ? 0.55 : 1,
                                overflow: "hidden",
                                outline: "none",
                                WebkitTapHighlightColor: "transparent",
                                background: checked
                                    ? "linear-gradient(135deg, #1a3a5c 0%, #1e5f8a 100%)"
                                    : "var(--vz-secondary-bg)",
                                boxShadow: checked
                                    ? "inset 0 1px 2px rgba(0,0,0,0.25), 0 2px 8px rgba(30,95,138,0.35)"
                                    : "inset 0 1px 3px rgba(0,0,0,0.12)",
                                transition:
                                    "background 280ms cubic-bezier(.4,0,.2,1), border-color 280ms ease, box-shadow 280ms cubic-bezier(.4,0,.2,1), opacity 180ms ease",
                            }}
                        >
                            {/* Gloss sheen — only on the ON state */}
                            {checked && (
                                <span
                                    aria-hidden="true"
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: "52%",
                                        borderRadius: `${height / 2}px ${height / 2}px 40% 40% / ${height / 2}px ${height / 2}px 12px 12px`,
                                        background:
                                            "linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.04) 100%)",
                                        pointerEvents: "none",
                                    }}
                                />
                            )}

                            {/* Label — centered in the space the knob isn't occupying */}
                            {hasText && (
                                <span
                                    aria-hidden="true"
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        bottom: 0,
                                        left: checked ? 0 : knob + pad,
                                        right: checked ? knob + pad : 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: Math.round(height * 0.36),
                                        fontWeight: 600,
                                        letterSpacing: 0.6,
                                        lineHeight: 1,
                                        whiteSpace: "nowrap",
                                        color: checked
                                            ? "#ffffff"
                                            : "var(--vz-secondary-color)",
                                        textShadow: checked
                                            ? "0 1px 1px rgba(0,0,0,0.28)"
                                            : "none",
                                        userSelect: "none",
                                        pointerEvents: "none",
                                        transition:
                                            "left 280ms cubic-bezier(.4,0,.2,1), right 280ms cubic-bezier(.4,0,.2,1), color 200ms ease",
                                    }}
                                >
                                    {checked ? onLabel : offLabel}
                                </span>
                            )}

                            {/* Knob */}
                            <span
                                aria-hidden="true"
                                style={{
                                    position: "absolute",
                                    top: pad - 1,
                                    left: checked ? trackWidth - knob - pad - 3 : pad - 1,
                                    width: knob,
                                    height: knob,
                                    borderRadius: "50%",
                                    background: checked
                                        ? "linear-gradient(180deg, #ffffff 0%, #f2f4f7 55%, #e3e7ed 100%)"
                                        : "var(--vz-tertiary-color)",
                                    boxShadow: checked
                                        ? "0 2px 5px rgba(16,24,40,0.28), inset 0 1px 0 rgba(255,255,255,0.95)"
                                        : "0 1px 3px rgba(0,0,0,0.30)",
                                    transition:
                                        "left 280ms cubic-bezier(.4,0,.2,1), background 280ms ease",
                                    pointerEvents: "none",
                                }}
                            />
                        </button>
                    );
                }}
            />

            <style>{`
                .dxo-switch:focus-visible {
                    border-color: #1e5f8a !important;
                    box-shadow: 0 0 0 3px rgba(30,95,138,0.40) !important;
                }
                .dxo-switch:not(:disabled):hover {
                    border-color: var(--vz-secondary-color);
                }
                .dxo-switch:not(:disabled):active > span:last-child {
                    transform: scale(0.94);
                }
                .dxo-switch > span:last-child {
                    transition: left 280ms cubic-bezier(.4,0,.2,1), background 280ms ease, transform 120ms ease;
                }
                @media (prefers-reduced-motion: reduce) {
                    .dxo-switch, .dxo-switch > span { transition: none !important; }
                }
            `}</style>
        </div>
    );
};

export default SwitchButton;